import { Card, Radio, Select, Space, Table } from "@arco-design/web-react";
import { Number, Currency } from "@/components/Numeric";
import { useCallback, useEffect, useMemo, useState } from "react";
import { CustomerAPI } from "@/apis/sales";
import { ItemAPI } from "@/apis/material";
import { ReportAPI } from "@/apis/report";
import dayjs from "dayjs";
import _ from "lodash";
import SaleHistoryChart from "./chart";

const PERIOD_OPTIONS = [
    { label: "Last 6 Months", value: 6 },
    { label: "Last 12 Months", value: 12 },
    { label: "Last 24 Months", value: 24 },
];

const GROUP_OPTIONS = [
    { label: "Customer", value: "customer" },
    { label: "Item", value: "item" },
];

const METRIC_OPTIONS = [
    { label: "Qty", value: "qty" },
    { label: "Amount", value: "amount" },
];

export const SaleHistory = () => {
    const [groupBy, setGroupBy] = useState("customer");
    const [metric, setMetric] = useState("qty");
    const [months, setMonths] = useState(6);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [customers, setCustomers] = useState([]);
    const [items, setItems] = useState([]);
    const [selectedCustomerId, setSelectedCustomerId] = useState();
    const [selectedItemId, setSelectedItemId] = useState();

    useEffect(() => {
        const fetchOptions = async () => {
            const [customerList, itemList] = await Promise.all([
                CustomerAPI.actives(),
                ItemAPI.actives(),
            ]);
            setCustomers(_.sortBy(customerList, "customerName"));
            setItems(_.sortBy(itemList, "itemName"));
        };
        fetchOptions();
    }, []);

    const getData = useCallback(async () => {
        setLoading(true);
        try {
            const params = { groupBy, metric, months };
            if (groupBy === "customer" && selectedCustomerId) {
                params.customerId = selectedCustomerId;
            }
            if (groupBy === "item" && selectedItemId) {
                params.itemId = selectedItemId;
            }
            const result = await ReportAPI.getSaleHistory(params);
            setData(result);
        } catch (error) {
            console.error(error);
        } finally {
            setLoading(false);
        }
    }, [groupBy, metric, months, selectedCustomerId, selectedItemId]);

    useEffect(() => {
        getData();
    }, [getData]);

    const { groups, monthList, pivotValues } = useMemo(() => {
        const groupSet = new Set();
        const monthSet = new Set();

        (data || []).forEach((item) => {
            groupSet.add(item.groupName);
            monthSet.add(item.month);
        });

        const groups = Array.from(groupSet).sort();
        const monthList = Array.from(monthSet).sort();

        if (monthList.length > 0) {
            const start = dayjs(monthList[0] + "-01");
            const end = dayjs(monthList[monthList.length - 1] + "-01");
            const fullMonths = [];
            let current = start;
            while (current.isBefore(end) || current.isSame(end, "month")) {
                fullMonths.push(current.format("YYYY-MM"));
                current = current.add(1, "month");
            }
            monthList.length = 0;
            monthList.push(...fullMonths);
        }

        const pivotValues = {};
        groups.forEach((group) => {
            pivotValues[group] = {};
            monthList.forEach((month) => {
                pivotValues[group][month] = 0;
            });
        });

        (data || []).forEach((item) => {
            if (pivotValues[item.groupName] && monthList.includes(item.month)) {
                const value = metric === "amount"
                    ? parseFloat(item.amount) || 0
                    : parseFloat(item.quantity) || 0;
                pivotValues[item.groupName][item.month] = value;
            }
        });

        return { groups, monthList, pivotValues };
    }, [data, metric]);

    const columns = useMemo(() => {
        const baseColumns = [
            {
                title: "Month",
                dataIndex: "month",
                fixed: true,
                width: 100,
            },
        ];
        const groupColumns = groups.map((group) => ({
            title: group,
            dataIndex: group,
            render: (val) => metric === "amount"
                ? <Currency value={val} />
                : <Number value={val} />,
        }));
        return [...baseColumns, ...groupColumns];
    }, [groups, metric]);

    const tableData = useMemo(() => {
        return monthList.map((month) => {
            const row = { month, key: month };
            groups.forEach((group) => {
                row[group] = pivotValues[group]?.[month] || 0;
            });
            return row;
        });
    }, [monthList, groups, pivotValues]);

    const handleGroupByChange = (val) => {
        setGroupBy(val);
        setSelectedCustomerId(undefined);
        setSelectedItemId(undefined);
    };

    const handleMetricChange = (val) => {
        setMetric(val);
    };

    const handlePeriodChange = (val) => {
        setMonths(val);
    };

    return (
        <Card title="Sale History"
            extra={
                <Space>
                    <span>Group By:</span>
                    <Radio.Group type="button" value={groupBy} onChange={handleGroupByChange} options={GROUP_OPTIONS} />
                    {groupBy === "customer" && (
                        <>
                            <Select
                                showSearch
                                allowClear
                                placeholder="All Customers"
                                style={{ width: 200 }}
                                value={selectedCustomerId}
                                filterOption={(inputValue, option) =>
                                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                }
                                onChange={(value) => setSelectedCustomerId(value)}
                                options={customers.map((c) => ({
                                    label: c.customerName,
                                    value: c.id,
                                }))}
                            />
                        </>
                    )}
                    {groupBy === "item" && (
                        <>
                            <Select
                                showSearch
                                allowClear
                                placeholder="All Items"
                                style={{ width: 200 }}
                                value={selectedItemId}
                                filterOption={(inputValue, option) =>
                                    option.props.children.toLowerCase().indexOf(inputValue.toLowerCase()) >= 0
                                }
                                onChange={(value) => setSelectedItemId(value)}
                                options={items.map((i) => ({
                                    label: i.itemName,
                                    value: i.id,
                                }))}
                            />
                        </>
                    )}
                    <span style={{ marginLeft: 8 }}>Metric:</span>
                    <Radio.Group type="button" value={metric} onChange={handleMetricChange} options={METRIC_OPTIONS} />
                    <span style={{ marginLeft: 8 }}>Period:</span>
                    <Select value={months} onChange={handlePeriodChange} style={{ width: 160 }}>
                        {PERIOD_OPTIONS.map((opt) => (
                            <Select.Option key={opt.value} value={opt.value}>{opt.label}</Select.Option>
                        ))}
                    </Select>
                </Space>
            }>
            {groups.length > 0 && (
                <div style={{ marginBottom: 16, overflowX: "auto" }}>
                    <SaleHistoryChart
                        groups={groups}
                        months={monthList}
                        values={pivotValues}
                        metric={metric}
                    />
                </div>
            )}
            <Table
                columns={columns}
                data={tableData}
                rowKey="key"
                hover={false}
                loading={loading}
                pagination={false}
                scroll={{ x: "max-content", y: 'calc(100vh - 300px)' }}
            />
        </Card>
    );
};
